import React, { forwardRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { useTheme } from '../ThemeContext';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

interface NetProfitChartProps {
  labels: string[];
  data: number[];
}

const NetProfitChart = forwardRef<any, NetProfitChartProps>(({ labels, data }, ref) => {
  const { chart } = useTheme();

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Net Profit/Loss',
        data,
        backgroundColor: data.map(v => v >= 0 ? 'rgba(16, 185, 129, 0.7)' : 'rgba(239, 68, 68, 0.7)'),
        borderColor: data.map(v => v >= 0 ? '#10b981' : '#ef4444'),
        borderWidth: 1,
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: chart.tooltipBg,
        titleColor: chart.tooltipTitle,
        bodyColor: chart.tooltipBody,
        borderColor: chart.tooltipBorder,
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        titleFont: { family: 'Inter', size: 13, weight: '600' },
        bodyFont: { family: 'Inter', size: 12 },
        callbacks: {
          label: (ctx: any) => {
            const val = ctx.parsed.y;
            const prefix = val >= 0 ? 'Laba' : 'Rugi';
            return `${prefix}: Rp ${(Math.abs(val) / 1e6).toFixed(1)} Jt`;
          },
        },
      },
      datalabels: {
        anchor: 'end',
        align: 'end',
        offset: 8,
        font: {
          family: 'Inter',
          size: 11,
          weight: '500',
        },
        color: (context: any) => context.dataset.borderColor?.[context.dataIndex] || '#666',
        formatter: (value: any) => {
          if (value === 0 || value === null) return '';
          return `Rp ${(Math.abs(value) / 1e6).toFixed(1)} Jt`;
        },
      },
    },
    scales: {
      x: {
        ticks: { color: chart.tickColor, font: { family: 'Inter', size: 11 } },
        grid: { color: chart.gridColorX },
      },
      y: {
        ticks: {
          color: chart.tickColor,
          font: { family: 'Inter', size: 11 },
          callback: (val: number) => `Rp ${(val / 1e6).toFixed(0)}Jt`,
        },
        grid: { color: chart.gridColorY },
      },
    },
  };

  return (
    <div className="chart-card animate-in" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="chart-card-header">
        <div>
          <div className="chart-card-title">📊 Net Profit / Loss</div>
          <div className="chart-card-subtitle">Laba/rugi bersih per bulan</div>
        </div>
      </div>
      <div className="chart-wrapper bar-chart">
        <Bar ref={ref} data={chartData} options={options} />
      </div>
    </div>
  );
});

NetProfitChart.displayName = 'NetProfitChart';

export default NetProfitChart;
